import {
  Box,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { Close, ExpandLess, ExpandMore, FolderOpen } from "@mui/icons-material";
import React, { useState } from "react";
import { getIconFromType } from "src/views/datasets/components/DatasetTypeChip";

/**
 * Dataset type for the container
 */
export interface Dataset {
  id: number;
  name: string;
  description?: string;
  dataset_type?: {
    name: string;
    [key: string]: any;
  };
  [key: string]: any;
}

interface SelectedDatasetsContainerProps {
  datasets: Dataset[];
  onRemove: (datasetId: number) => void;
  title?: string;
  icon?: React.ReactNode;
}

/**
 * DatasetListItem - Individual dataset item in the list
 */
const DatasetListItem: React.FC<{
  dataset: Dataset;
  onRemove: (datasetId: number) => void;
}> = ({ dataset, onRemove }) => {
  // Truncate description to 100 characters
  const truncateDescription = (desc: string | undefined): string => {
    if (!desc) return "No description";
    if (desc.length <= 100) return desc;
    return desc.substring(0, 100) + "...";
  };

  const datasetTypeName = dataset.dataset_type?.name || "document";
  const datasetIcon = getIconFromType(datasetTypeName, true, 18);

  return (
    <Tooltip title={dataset.name} arrow placement="top" enterDelay={500}>
      <Box
        data-testid={`rfp-dataset-item-${dataset.id}`}
        sx={{
          p: 1.5,
          borderRadius: 1,
          backgroundColor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          "&:hover": {
            borderColor: "primary.main",
            backgroundColor: "action.hover",
          },
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          {/* Dataset Icon - based on dataset type */}
          <Box sx={{ flexShrink: 0 }}>{datasetIcon}</Box>

          {/* Dataset Information */}
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Stack spacing={0.5} alignItems="flex-start">
              {/* Dataset Name */}
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: "text.primary",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  width: "100%",
                  textAlign: "left",
                }}
              >
                {dataset.name}
              </Typography>

              {/* Dataset Description */}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  textAlign: "left",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  wordBreak: "break-word",
                }}
              >
                {truncateDescription(dataset.description)}
              </Typography>
            </Stack>
          </Box>

          {/* Remove Button */}
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onRemove(dataset.id);
            }}
            sx={{
              color: "text.secondary",
              "&:hover": {
                color: "error.main",
                backgroundColor: "error.lighter",
              },
            }}
            data-testid={`rfp-dataset-remove-${dataset.id}`}
            data-attr="assistant:btn:remove-selected-dataset"
          >
            <Close fontSize="small" />
          </IconButton>
        </Stack>
      </Box>
    </Tooltip>
  );
};

/**
 * SelectedDatasetsContainer - Displays selected datasets in a vertical list
 * similar to AttachmentsContainer/AttachmentDrawer
 */
const SelectedDatasetsContainer: React.FC<SelectedDatasetsContainerProps> = ({
  datasets,
  onRemove,
  icon,
}) => {
  const [isExpanded, setIsExpanded] = useState(datasets.length <= 3);

  if (!datasets || datasets.length === 0) return null;

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const countText =
    datasets.length === 1
      ? "1 dataset selected"
      : `${datasets.length} datasets selected`;

  return (
    <Paper
      variant="outlined"
      sx={{
        mb: 1,
        borderColor: "divider",
        backgroundColor: "background.default",
        borderRadius: 1,
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        onClick={toggleExpanded}
        sx={{
          px: 2,
          py: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          "&:hover": {
            backgroundColor: "action.hover",
          },
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          {icon || <FolderOpen fontSize="small" color="primary" />}
          <Typography variant="body2" color="text.secondary" fontWeight={500}>
            {countText}
          </Typography>
        </Stack>
        <IconButton
          size="small"
          onClick={toggleExpanded}
          data-attr="assistant:btn:toggle-selected-datasets"
        >
          {isExpanded ? (
            <ExpandLess fontSize="small" />
          ) : (
            <ExpandMore fontSize="small" />
          )}
        </IconButton>
      </Box>

      <Divider />

      {/* Dataset List */}
      <Collapse in={isExpanded}>
        <Stack spacing={1} sx={{ p: 1 }}>
          {datasets.map((dataset) => (
            <DatasetListItem
              key={`dataset-${dataset.id}`}
              dataset={dataset}
              onRemove={onRemove}
            />
          ))}
        </Stack>
      </Collapse>

      {/* Collapsed Preview */}
      {!isExpanded && (
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {datasets
              .slice(0, 3)
              .map((d) => d.name)
              .join(", ")}
            {datasets.length > 3 && ` +${datasets.length - 3} more`}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default SelectedDatasetsContainer;
